import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "../components/common/LivoraToast";

/**
 * Call-return-only: restore scroll + optional short toast from navigation state.
 * Does not affect unrelated navigation.
 */
export default function CallReturnRestorer() {
  const location = useLocation();
  const { toast, showToast } = useLivoraToast(2800);
  const lastKeyRef = useRef("");

  useEffect(() => {
    const scroll = location.state?.callReturnScroll;
    const message = location.state?.callReturnMessage;
    const key = `${location.key || ""}:${scroll?.y ?? ""}:${message || ""}`;
    if (!scroll && !message) return;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    if (typeof message === "string" && message.trim()) {
      showToast("info", message.trim());
    }

    if (!scroll || (scroll.x === 0 && scroll.y === 0)) {
      return undefined;
    }

    const targetX = Math.max(0, Math.round(Number(scroll.x) || 0));
    const targetY = Math.max(0, Math.round(Number(scroll.y) || 0));
    let attempts = 0;
    const maxAttempts = 8;
    let raf = 0;
    let timeoutId = 0;

    const apply = () => {
      attempts += 1;
      try {
        window.scrollTo(targetX, targetY);
      } catch (_e) {
        /* ignore */
      }
      const curY =
        Math.round(window.scrollY || window.pageYOffset || 0);
      if (Math.abs(curY - targetY) <= 2 || attempts >= maxAttempts) {
        console.log(
          `[callReturn] scroll_restored x=${targetX} y=${curY} attempts=${attempts}`
        );
        return;
      }
      timeoutId = window.setTimeout(() => {
        raf = window.requestAnimationFrame(apply);
      }, 50);
    };

    raf = window.requestAnimationFrame(apply);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [location, showToast]);

  return <LivoraToast toast={toast} />;
}
